import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { Search } from "lucide-react";
import type { SlideProps } from "@/engine/types";
import { SlideShell } from "@/ui";

const PHRASES = ["best running shoes for flat feet", "seo — beginner"] as const;

/** Character-by-character typing loop, gated on `active`. */
function useTypewriter(active: boolean): string {
  const [text, setText] = useState("");

  useEffect(() => {
    if (!active) {
      setText("");
      return;
    }
    let phraseIdx = 0;
    let charIdx = 0;
    let deleting = false;
    let timer: ReturnType<typeof setTimeout>;

    const tick = () => {
      const phrase = PHRASES[phraseIdx % PHRASES.length]!;
      if (!deleting) {
        charIdx += 1;
        setText(phrase.slice(0, charIdx));
        if (charIdx >= phrase.length) {
          deleting = true;
          timer = setTimeout(tick, 2000); // linger on the full query
        } else {
          timer = setTimeout(tick, 75 + Math.random() * 55);
        }
      } else {
        charIdx -= 1;
        setText(phrase.slice(0, charIdx));
        if (charIdx <= 0) {
          deleting = false;
          phraseIdx += 1;
          timer = setTimeout(tick, 650); // brief empty pause
        } else {
          timer = setTimeout(tick, 32);
        }
      }
    };

    timer = setTimeout(tick, 900);
    return () => clearTimeout(timer);
  }, [active]);

  return text;
}

const FLOATING_CHIPS = [
  { label: "what is page authority", x: "8%", y: "18%", delay: 0, duration: 9 },
  { label: "how do search engines rank pages", x: "72%", y: "14%", delay: 1.4, duration: 11 },
  { label: "internal linking basics", x: "10%", y: "74%", delay: 2.2, duration: 10 },
  { label: "e-e-a-t checklist", x: "78%", y: "70%", delay: 0.8, duration: 12 },
] as const;

export function TitleSlide({ active }: SlideProps) {
  const typed = useTypewriter(active);

  return (
    <SlideShell chapter={1} kicker="" align="center">
      <div className="relative flex h-full w-full flex-col items-center justify-center">
        {/* Floating background query chips */}
        {FLOATING_CHIPS.map((chip) => (
          <motion.div
            key={chip.label}
            className="glass pointer-events-none absolute px-6 py-3 font-mono text-xl text-white/30"
            style={{ left: chip.x, top: chip.y }}
            initial={{ opacity: 0 }}
            animate={active ? { opacity: [0, 1, 1], y: [0, -14, 0] } : { opacity: 0 }}
            transition={{
              opacity: { delay: chip.delay + 0.8, duration: 1.2 },
              y: {
                delay: chip.delay,
                duration: chip.duration,
                repeat: Infinity,
                ease: "easeInOut",
              },
            }}
          >
            {chip.label}
          </motion.div>
        ))}

        {/* Pulsing glow behind the hero */}
        <motion.div
          className="pointer-events-none absolute h-[420px] w-[760px] rounded-full"
          style={{
            background:
              "radial-gradient(closest-side, rgba(34,211,238,0.16), rgba(59,130,246,0.08), transparent)",
          }}
          animate={active ? { scale: [1, 1.12, 1], opacity: [0.7, 1, 0.7] } : { opacity: 0 }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Search bar */}
        <motion.div
          className="glass relative z-10 flex w-[760px] max-w-full items-center gap-5 px-8 py-6"
          initial={{ opacity: 0, y: 28, filter: "blur(8px)" }}
          animate={
            active
              ? { opacity: 1, y: 0, filter: "blur(0px)" }
              : { opacity: 0, y: 28, filter: "blur(8px)" }
          }
          transition={{ duration: 0.7, ease: [0.32, 0.72, 0, 1] }}
          style={{
            boxShadow:
              "0 8px 32px rgba(0,0,0,0.35), 0 0 64px -12px #22d3ee55, inset 0 1px 0 rgba(255,255,255,0.08)",
          }}
        >
          <Search className="h-8 w-8 shrink-0 text-accent-cyan" strokeWidth={2.2} />
          <span className="font-mono text-3xl text-white/90">
            {typed}
            <motion.span
              className="ml-1 inline-block h-8 w-[3px] translate-y-1 rounded-full bg-accent-cyan align-baseline"
              animate={{ opacity: [1, 1, 0, 0] }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
          </span>
        </motion.div>

        {/* Hero */}
        <motion.h1
          className="text-gradient z-10 mt-14 font-display text-8xl font-bold tracking-tight"
          initial={{ opacity: 0, y: 32, filter: "blur(10px)" }}
          animate={
            active
              ? { opacity: 1, y: 0, filter: "blur(0px)" }
              : { opacity: 0, y: 32, filter: "blur(10px)" }
          }
          transition={{ delay: 0.25, duration: 0.8, ease: [0.32, 0.72, 0, 1] }}
        >
          SEO — Beginner
        </motion.h1>

        <motion.p
          className="z-10 mt-7 max-w-5xl text-3xl leading-relaxed text-white/80"
          initial={{ opacity: 0, y: 24 }}
          animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
          transition={{ delay: 0.45, duration: 0.7, ease: [0.32, 0.72, 0, 1] }}
        >
          How search fits your <span className="font-semibold text-white">frontend</span> and{" "}
          <span className="font-semibold text-white">content</span> work — not a magic rank
          button.
        </motion.p>

        {/* Footer */}
        <motion.div
          className="absolute bottom-2 z-10 flex w-full items-center justify-center gap-10 text-xl text-white/40"
          initial={{ opacity: 0 }}
          animate={active ? { opacity: 1 } : { opacity: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
        >
          {/* <span>Emre</span>
          <span className="h-1 w-1 rounded-full bg-white/30" /> */}
          <span>→ to begin</span>
        </motion.div>
      </div>
    </SlideShell>
  );
}
